import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { useNavigate } from 'react-router-dom';
import { useCurrencySymbol } from '../../../../utils/currencyUtils';
import useWizardCommaConvert from '../../../../utils/useWizardCommaConvert';
import useMatchedProject from '../useMatchedProject';
import { resetcurrentLeadID, setCurrentPageTitle } from '../../../../store/slices/pageSlice';
import { fetchSalesTaxes } from '../../../../store/slices/salesTaxesSlice';
import { fetchDescriptionTasks } from '../../../../store/slices/DescriptionTask';
import { fetchAllProjects } from '../../../../store/slices/projectsSlice';
import { fetchEmployees } from '../../../../store/slices/employeeSlice';
import { fetchWorkEntries } from '../../../../store/slices/workentriesSlice';
import { fetchHardwareDescriptions } from '../../../../store/slices/hardwareDescriptionSlice';
import { fetchQuotations } from '../../../../store/slices/hourlyRates';
import { fetchLeads, updateLead, updateQuotedExpense } from '../../../../store/slices/leadsSlice';
import { clearWizardState, goNext, goToStep, setCreatedProject, setOperationProjectStatus } from '../../../../store/slices/WizardSlice/wizardStepSlice';
import QuotationWizardBasePlan from './QuotationWizardBasePlan';
import { fetchGroups } from '../../../../store/slices/groupSlice';
import useSubscriptionLevel from '../../../../utils/SubscriptionLevel';

const PandLWizardBasePlan = () => {
    const currency = useCurrencySymbol();
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const subscriptionPackage = useSubscriptionLevel();
    const USAComma = useWizardCommaConvert();
    const createdProject = useSelector((state) => state.wizardstep.createdProject);
    const wizardPlanState = useSelector((state) => state.wizardstep.wizardPlanState);
    const wizardProjectState = useSelector((state) => state.wizardstep.wizardProjectState);
    const { taxes } = useSelector((state) => state.salesTaxes);
    const operationProjectName = createdProject?.[0] || null;
    const { group: groups } = useSelector((state) => state.group);
    const { SalesStatus } = useMatchedProject(operationProjectName);
    const [calculatedFinanceProjects, setCalculatedFinanceProjects] = useState([]);

    console.log("calculatedFinanceProjects", calculatedFinanceProjects)

    useEffect(() => {
        dispatch(setCurrentPageTitle("Profit and Loss(P&L) Tracker"));
        dispatch(fetchSalesTaxes());
        dispatch(fetchDescriptionTasks());
        dispatch(fetchAllProjects());
        dispatch(fetchEmployees());
        dispatch(fetchWorkEntries());
        dispatch(fetchHardwareDescriptions());
        dispatch(fetchQuotations());
        dispatch(fetchGroups());
    }, [dispatch]);

    useEffect(() => {
        if (subscriptionPackage === "CRM") {
            dispatch(resetcurrentLeadID());
        }
    }, [dispatch, subscriptionPackage]);

    const groupNameById = React.useMemo(() => {
        const map = new Map();
        (groups || []).forEach((g) => {
            const id = g?._id || g?.id;
            const name = g?.newGroupName || g?.groupname || g?.groupName || g?.name || "";
            if (id && name) map.set(String(id), name);
        });
        return map;
    }, [groups]);

    const getDisplayGroupName = React.useCallback((item) => {
        const groupId = item?.groupid?._id || item?.groupid;
        if (groupId && groupNameById.has(String(groupId))) {
            return groupNameById.get(String(groupId));
        }
        return item?.AssignedGroup || "";
    }, [groupNameById]);

    const [showQuoteModal, setShowQuoteModal] = useState(false);

    const [quotedExpensesValue, setQuotedExpensesValue] = useState({})
    const [quotedValueState, setQuotedValueState] = useState({})

    const [GandATax, setGandATax] = useState(0);
    const [taxValue, setTaxValue] = useState(0);

    useEffect(() => {
        const norm = (name) => (name || "").toString().trim().toLowerCase();
        const toPct = (val) => (typeof val === "number" ? val : Number(val)) || 0;
        // Defaults from G&A/Tax settings when tax table is empty or has no matching rows
        let gaPct = 15;
        let taxPct = 27;
        if (Array.isArray(taxes) && taxes.length > 0) {
            taxes.forEach((data) => {
                if (!data) return;
                const name = norm(data.taxName ?? data.tax_name);
                const pct = toPct(data.taxPercentage ?? data.tax_percentage);
                if (name === norm("G&A Expenses")) gaPct = pct;
                else if (name === norm("Tax")) taxPct = pct;
            });
        }
        setGandATax(gaPct);
        setTaxValue(taxPct);
    }, [taxes]);


    useEffect(() => {
        if (!createdProject) return;

        const financeObject = createdProject.map((proj) => {
            const projectId = proj?._id ?? proj?.id;
            const projectName = proj?.ProjectName;
            // const QuotedValue = proj?.QuotedValue || 0;
            const QuotedValue =
                quotedValueState[proj._id] !== undefined
                    ? Number(quotedValueState[proj._id])
                    : Number(proj?.QuotedValue) || 0;

            // If input is typed → override proj.quotedExpense
            const quotedExpense =
                quotedExpensesValue[proj._id] !== undefined
                    ? Number(quotedExpensesValue[proj._id])
                    : proj?.quotedExpense || 0;

            const grossProfit = QuotedValue - quotedExpense;
            const GAExpenses = grossProfit * (GandATax / 100);
            const TaxCalculated = grossProfit * (taxValue / 100);
            const EBIT = grossProfit - (grossProfit * GandATax) / 100;

            const netRevenueInDollar = EBIT - (grossProfit * taxValue) / 100;
            const netRevenueInPercentage = (netRevenueInDollar / QuotedValue) * 100;

            return {
                projectId,
                projectName,
                QuotedValue,
                quotedExpense,
                grossProfit,
                GAExpenses,
                TaxCalculated,
                netRevenueInDollar,
                netRevenueInPercentage,
            };
        });
        setCalculatedFinanceProjects(financeObject);
    }, [createdProject, GandATax, taxValue, quotedExpensesValue, quotedValueState]);

    const handleQuotedExpenses = (id, value) => {
        setQuotedExpensesValue((prev) => ({
            ...prev,
            [id]: value
        }));
    };

    const handleQuotedValue = (id, value) => {
        setQuotedValueState((prev) => ({
            ...prev,
            [id]: value
        }));
    };

    const SubmitQuotedExpenses = async (proj, value) => {
        try {
            // Handle empty string or undefined - convert to 0
            const expenseValue = value === "" || value === undefined || value === null ? 0 : Number(value);
            await dispatch(updateQuotedExpense({
                id: proj.id,       // FIXED HERE
                quotedExpense: expenseValue
            }));
            dispatch(fetchLeads());  // this repopulates createdProject correctly
        } catch (error) {
            console.error("Error:", error);
        }
    };
    const SubmitQuotedValueState = async (proj, value) => {
        const updatedLeadData = {
            ProjectName: proj?.ProjectName,
            QuotedValue: Number(value),
            quotationId: proj?.quotationId,
        }
        try {
            await dispatch(updateLead({
                id: proj.id,       // FIXED HERE
                updatedData: updatedLeadData
            }));
            dispatch(fetchLeads());  // this repopulates createdProject correctly
        } catch (error) {
            console.error("Error:", error);
        }
    };

    const handleNext = () => {
        dispatch(setOperationProjectStatus(SalesStatus))
        dispatch(setCreatedProject(createdProject));
        dispatch(goNext())
    }

    const getNetRevenueClass = (value) => {
        if (value < 0) return "text-red-600";
        if (value >= 0 && value < 10) return "text-orange-500";
        return "text-black";
    };
    const getNetRevenueInDollarClass = (value) => {
        if (value < 0) return "text-red-600";
        return "text-black";
    };


    return (
        <div>
            <div className="w-full min-h-[10vh] max-h-[80vh] overflow-auto pt-6 pb-4">
                <div className="w-full head sticky top-0 z-10 flex text-black bg-[#E8E8E8]">
                    <div className="flex-shrink-0 w-10 px-3 py-2 flex items-center justify-center text-center border text-xs font-bold  tracking-wider bg-[#E8E8E8]">
                        Sr. No
                    </div>
                    <div className="flex-shrink-0 w-40 px-3 py-2 flex gap-2  justify-center align-middle items-center text-center border text-xs font-bold  tracking-wider capitalize bg-[#E8E8E8]">
                        Group Name
                    </div>
                    <div className="flex-shrink-0 w-40 px-3 py-2 flex gap-2  justify-center align-middle items-center text-center border text-xs font-bold  tracking-wider capitalize bg-[#E8E8E8]">
                        Client Name
                    </div>
                    <div className="flex-shrink-0 w-40 px-3 py-2 flex gap-2  justify-center align-middle items-center text-center border text-xs font-bold  tracking-wider capitalize bg-[#E8E8E8]">
                        Project Name
                    </div>
                    <div className="flex-shrink-0 w-40 px-3 py-2 gap-2 flex justify-center align-middle items-center text-center border text-xs font-bold  tracking-wider capitalize bg-[#E8E8E8]">
                        Quoted Price({currency})
                    </div>
                    <div className="flex-shrink-0 w-40 px-3 py-2 flex justify-center align-middle items-center text-center border text-xs font-bold  tracking-wider capitalize bg-[#E8E8E8]">
                        {`Quoted Expenses(${currency})`}
                    </div>
                    <div className="flex-shrink-0 w-40 px-3 py-2 flex justify-center align-middle items-center text-center border text-xs font-bold  tracking-wider capitalize bg-[#E8E8E8]">
                        Gross Profit({currency})
                    </div>
                    <div className="flex-shrink-0 w-40 px-3 py-2 flex justify-center align-middle items-center text-center border text-xs font-bold  tracking-wider capitalize bg-[#E8E8E8]">
                        G&A Expenses({currency})
                    </div>
                    <div className="flex-shrink-0 w-24 px-3 py-2 flex justify-center align-middle items-center text-center border text-xs font-bold  tracking-wider capitalize bg-[#E8E8E8]">
                        Tax({currency})
                    </div>
                    <div className="flex-shrink-0 w-44 flex flex-col justify-center align-middle items-center text-center border text-xs font-bold  tracking-wider capitalize bg-[#E8E8E8]">
                        <div className="h-[50%] w-full flex justify-center align-middle items-center"> Net Revenue</div>
                        <div className="h-[50%] flex w-full">
                            <div className="w-[50%] border flex justify-center align-middle items-center">({currency})</div>
                            <div className="w-[50%] border flex justify-center align-middle items-center">(%)</div>
                        </div>

                    </div>
                    <div className="flex-shrink-0 w-28 px-3 py-2 flex justify-center align-middle items-center text-center border text-xs font-bold  tracking-wider capitalize bg-[#E8E8E8]">
                        Notes
                    </div>
                    <div className="flex-shrink-0 border w-24 px-3 py-3 flex justify-center align-middle items-center text-center  text-xs font-bold  tracking-wider capitalize bg-[#E8E8E8]">
                        Quotation

                    </div>
                </div>

                {createdProject?.map((proj, index) => {
                    const matchedRow = calculatedFinanceProjects?.find((row) => row?.projectId === (proj?._id ?? proj?.id))
                    console.log("calculatedFinanceProjects matchedRow", matchedRow)

                    return (
                        <div
                            key={index}
                            className="w-full flex text-black bg-white min-h-[8vh]">
                            <div className="flex-shrink-0 w-10 px-3 py-2 flex items-center justify-center text-center border-b text-xs tracking-wider ">
                                {index + 1}
                            </div>
                            <div className="flex-shrink-0 w-40 px-3 py-2 flex gap-2  justify-center align-middle items-center text-center border-b text-xs tracking-wider capitalize ">
                                {getDisplayGroupName(proj)}
                            </div>
                            <div className="flex-shrink-0 w-40 px-3 py-2 flex gap-2  justify-center align-middle items-center text-center border-b text-xs  tracking-wider capitalize ">
                                {proj?.ClientName}
                            </div>
                            <div className="flex-shrink-0 w-40 px-3 py-2 flex gap-2  justify-center align-middle items-center text-center border-b text-xs  tracking-wider capitalize ">
                                {proj?.ProjectName}

                            </div>
                            <div className="bg-white break-all whitespace-normal flex-shrink-0 w-40 px-3 py-2 gap-2 flex justify-center align-middle items-center text-center border-b text-xs  tracking-wider capitalize ">
                                <input
                                    type="text"
                                    value={
                                        quotedValueState[proj._id] !== undefined
                                            ? USAComma(quotedValueState[proj._id])
                                            : USAComma(proj.QuotedValue)
                                    }
                                    className="p-1 text-center h-full w-full outline-none rounded-md border bg-[#cfecf7] border-black break-all whitespace-normal"
                                    onChange={(e) =>
                                        handleQuotedValue(
                                            proj._id,
                                            e.target.value.replace(/,/g, "").replace(/\D/g, "")
                                        )
                                    }
                                    onBlur={() => {
                                        SubmitQuotedValueState(proj, quotedValueState[proj._id]);
                                    }}

                                // onKeyDown={(e) => {
                                //     if (e.key === "Enter") {
                                //         SubmitQuotedValueState(proj, quotedValueState[proj._id]);
                                //     }
                                // }}
                                />
                            </div>

                            <div
                                className="bg-white  flex-shrink-0 w-40 px-3 py-2 flex justify-center align-middle items-center text-center border-b text-xs  tracking-wider capitalize">
                                <input
                                    type="text"
                                    placeholder='Enter Quoted Expenses'
                                    value={
                                        (() => {
                                            const currentValue = quotedExpensesValue[proj._id] !== undefined
                                                ? quotedExpensesValue[proj._id]
                                                : proj.quotedExpense;
                                            // If value is empty string, 0, or null/undefined, return empty string to show placeholder
                                            if (currentValue === "" || currentValue === 0 || currentValue === null || currentValue === undefined) {
                                                return "";
                                            }
                                            return USAComma(currentValue);
                                        })()
                                    }
                                    className="p-1 text-center h-full w-full outline-none rounded-md border bg-[#cfecf7] border-black break-all whitespace-normal"
                                    onChange={(e) =>
                                        handleQuotedExpenses(
                                            proj._id,
                                            e.target.value.replace(/,/g, "").replace(/\D/g, "")
                                        )
                                    }
                                    onBlur={() => {
                                        const value = quotedExpensesValue[proj._id];
                                        SubmitQuotedExpenses(proj, value === "" ? 0 : (value || 0));
                                    }}
                                // onKeyDown={(e) => {
                                //     if (e.key === "Enter") {
                                //         SubmitQuotedExpenses(proj, quotedExpensesValue[proj._id]);
                                //     }
                                // }}
                                />
                            </div>

                            <div className={`${getNetRevenueInDollarClass(matchedRow?.grossProfit)} bg-white break-all whitespace-normal flex-shrink-0 w-40 px-3 py-2 flex justify-center align-middle items-center text-center border-b text-xs font-medium  tracking-wider capitalize `}>
                                {USAComma(matchedRow?.grossProfit)}
                            </div>
                            <div className={`${getNetRevenueInDollarClass(matchedRow?.GAExpenses)} bg-white break-all whitespace-normal flex-shrink-0 w-40 px-3 py-2 flex justify-center align-middle items-center text-center border-b text-xs font-medium  tracking-wider capitalize`} >
                                {USAComma(matchedRow?.GAExpenses)}
                            </div>
                            <div className={`${getNetRevenueInDollarClass(matchedRow?.TaxCalculated)} bg-white break-all whitespace-normal flex-shrink-0 w-24 px-3 py-2 flex justify-center align-middle items-center text-center border-b text-xs font-medium  tracking-wider capitalize `}>
                                {USAComma(matchedRow?.TaxCalculated)}
                            </div>
                            <div className="bg-white  flex-shrink-0 w-44 flex flex-col justify-center align-middle items-center text-center border-b text-xs  tracking-wider capitalize ">
                                <div className="flex w-full">
                                    <div className={`${getNetRevenueInDollarClass(matchedRow?.netRevenueInDollar)} w-[50%] flex break-all whitespace-normal justify-center align-middle items-center`}>
                                        {USAComma(matchedRow?.netRevenueInDollar)}
                                    </div>
                                    <div
                                        className={`${getNetRevenueClass(matchedRow?.netRevenueInPercentage)} w-[50%]  flex justify-center items-center break-all whitespace-normal`}
                                    >
                                        {matchedRow?.netRevenueInPercentage !== undefined && matchedRow?.netRevenueInPercentage !== null 
                                            ? parseFloat(matchedRow.netRevenueInPercentage).toFixed(2) 
                                            : ""}
                                    </div>
                                </div>
                            </div>
                            <div className="bg-white max-h-16 overflow-auto flex-shrink-0 w-28 px-3 py-2 break-all whitespace-normal flex justify-center align-middle items-center text-center border-b text-xs font-medium  tracking-wider capitalize ">
                                {proj?.comments}
                            </div>
                            <div
                                onClick={() => {
                                    const status = proj?.Status?.toLowerCase()?.trim();
                                    const isWon = status === "won" || status === "won recurring";
                                    if (wizardPlanState === "basicplan" && isWon) {
                                        return; // Disable click for Won status in CRM package
                                    }
                                    setShowQuoteModal(true);
                                }}
                                className={`bg-white flex-shrink-0 w-24 px-3 py-3 flex justify-center align-middle items-center text-center border-b text-xs  tracking-wider capitalize ${
                                    (() => {
                                        const status = proj?.Status?.toLowerCase()?.trim();
                                        const isWon = status === "won" || status === "won recurring";
                                        return wizardPlanState === "basicplan" && isWon;
                                    })()
                                        ? "cursor-not-allowed"
                                        : "cursor-pointer"
                                }`}>
                                <TextSnippetIcon className={
                                    (() => {
                                        const status = proj?.Status?.toLowerCase()?.trim();
                                        const isWon = status === "won" || status === "won recurring";
                                        return wizardPlanState === "basicplan" && isWon;
                                    })()
                                        ? "cursor-not-allowed text-gray-400"
                                        : "cursor-pointer"
                                } />
                            </div>
                        </div>
                    )
                })}
            </div>


            <div className='flex justify-end w-[99%]'>
                <div className='pt-2'>
                    <div className="flex gap-4">
                        <button
                            onClick={() => {
                                dispatch(setCreatedProject(createdProject));
                                dispatch(goToStep(1))
                            }}
                            className=" py-2 px-7 bg-black overflow-hidden shadow-md text-white rounded-lg"
                        >
                            Back
                        </button>
                        {(wizardPlanState === "basicplan" && wizardProjectState === "newProject") && (
                            <button
                                onClick={() => {
                                    if (wizardPlanState === "basicplan" && wizardProjectState === "newProject") {
                                        navigate("/dashboard")
                                        dispatch(clearWizardState())
                                        dispatch(resetcurrentLeadID())
                                    } else {
                                        handleNext()
                                    }
                                }}
                                className=" py-2 px-7 bg-[#e32278] overflow-hidden shadow-md text-white rounded-lg"
                            >
                                {wizardPlanState === "basicplan" && wizardProjectState === "newProject" ? "Save" : "Next"}
                            </button>
                        )}

                        {(wizardPlanState === "basicplan" && wizardProjectState === "existingProject") && (
                            <button
                                onClick={() => {
                                    if (wizardPlanState === "basicplan" && wizardProjectState === "existingProject") {
                                        navigate("/dashboard");
                                        dispatch(clearWizardState())
                                        dispatch(setCreatedProject([]));
                                        dispatch(resetcurrentLeadID());
                                    } else {
                                        handleNext()
                                    }
                                }}
                                className=" py-2 px-7 bg-[#e32278] overflow-hidden shadow-md text-white rounded-lg"
                            >
                                {wizardPlanState === "basicplan" && wizardProjectState === "existingProject" ? "Save" : "Next"}
                            </button>
                        )}

                    </div>
                </div>
            </div>

            {/* calculatedFinanceProjects */}
            {showQuoteModal && (
                <QuotationWizardBasePlan
                    setShowQuoteModal={setShowQuoteModal}
                    selectedProject={createdProject[0]?.ProjectName}
                    selectedGroup={getDisplayGroupName(createdProject?.[0] || {})}
                    calculatedFinanceProjects={calculatedFinanceProjects}
                />
            )}
        </div>
    )
}

export default PandLWizardBasePlan
